import React from "react";
import { Box, Button, Image, type ButtonProps } from "@chakra-ui/react";
import { landing } from "../../landingTokens";
import { useLandingTheme } from "../../LandingThemeContext";
import { useLandingCopy } from "../../LandingLanguageContext";
import { trackLandingCallToActionClick } from "../../handoff/appHandoff";
import { useAppUrl } from "../../handoff/useAppUrl";
import { NARROW_MEASURE, SECTION_GAP, ThemedBody, ThemedContainer, ThemedDisplayTitle } from "./ThemedLayout";
import { AppStoreCallToAction, WEB_APP_CTA_CLASS_NAME } from "../AppStoreCallToAction";

// The illustration's hills end in this green.
const ILLUSTRATION_BOTTOM_GREEN = "#1D4A29";

function ClosingCopy({
  headingColor,
  bodyColor,
  onDarkBand = false,
}: {
  headingColor: string;
  bodyColor: string;
  onDarkBand?: boolean;
}) {
  const appHomeHref = useAppUrl("/");
  const theme = useLandingTheme();
  const closingCallToActionCopy = useLandingCopy().closingCallToAction;
  const buttonBg = onDarkBand && !theme.isDark ? theme.color.surface : theme.color.accent;
  const buttonColor = onDarkBand && !theme.isDark ? theme.color.accentDark : theme.button.primaryColor;
  const buttonHoverBg = onDarkBand && !theme.isDark ? theme.color.surfaceAlt : theme.color.accentHover;
  const buttonStyle: ButtonProps = {
    mt: "32px",
    bg: buttonBg,
    color: buttonColor,
    border: theme.button.border,
    boxShadow: theme.button.shadow,
    fontFamily: theme.font.body,
    fontSize: "18px",
    fontWeight: 600,
    minH: "64px",
    h: "auto",
    maxW: "100%",
    px: { base: "24px", md: "40px" },
    py: "12px",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    lineHeight: "1.4",
    borderRadius: theme.shape.buttonRadius,
    transition: landing.buttonHoverTransition,
    _hover: { bg: buttonHoverBg, transform: theme.button.hoverTransform, textDecoration: "none" },
  };
  return (
    <Box maxW={NARROW_MEASURE} mx="auto" textAlign="center">
      <ThemedDisplayTitle color={headingColor}>{closingCallToActionCopy.heading}</ThemedDisplayTitle>
      <ThemedBody mt="14px" color={bodyColor}>
        {closingCallToActionCopy.body}
      </ThemedBody>
      <Button
        as="a"
        href={appHomeHref}
        className={WEB_APP_CTA_CLASS_NAME}
        onClick={() => trackLandingCallToActionClick("closing_upload_your_slides")}
        {...buttonStyle}
      >
        {closingCallToActionCopy.uploadSlidesButton}
      </Button>
      {/* Phone visitors from a Meta ad get the store link instead (AppStoreCallToAction.tsx), same style. */}
      <AppStoreCallToAction
        placement="closing_cta"
        label="get_the_app"
        onlyAfterHydration
        continueInBrowserColor={bodyColor}
        buttonProps={buttonStyle}
      />
    </Box>
  );
}

// One closing call to action. Illustrated themes put the copy on the mascot scene's sky and let the hills
// run into the footer; the others close with a full-bleed band in the page's darkest tone.
export function ThemedClosingCta() {
  const theme = useLandingTheme();
  const closingCallToActionCopy = useLandingCopy().closingCallToAction;

  if (theme.footer.showIllustration) {
    return (
      <Box as="section" mt={SECTION_GAP} position="relative" bg={theme.color.pageBg}>
        <Box position="relative" zIndex={1} px={{ base: "20px", md: "32px" }}>
          <ClosingCopy headingColor="#1B2E20" bodyColor="#3B4F41" />
        </Box>
        <Box position="relative" mt={{ base: "24px", md: "32px" }}>
          <Image
            loading="lazy"
            src="/images/landing/monkey-capybara.webp"
            alt={closingCallToActionCopy.capybaraAndMonkeyIllustrationAlt}
            w="100%"
            display="block"
          />
          {/* Blend the illustration's sky into the page ground so there is no seam. */}
          <Box
            aria-hidden="true"
            position="absolute"
            top={0}
            left={0}
            right={0}
            h="38%"
            bgGradient={`linear(to-b, ${theme.color.pageBg}, transparent)`}
          />
        </Box>
        {theme.color.footerBg !== ILLUSTRATION_BOTTOM_GREEN && (
          <Box h="120px" bgGradient={`linear(to-b, ${ILLUSTRATION_BOTTOM_GREEN}, ${theme.color.footerBg})`} />
        )}
      </Box>
    );
  }

  const bandBg = theme.isDark ? theme.color.surfaceAlt : theme.color.accentDark;
  const headingColor = theme.isDark ? theme.color.heading : "#FFFFFF";
  const bodyColor = theme.isDark ? theme.color.text : "rgba(255, 255, 255, 0.78)";
  return (
    <Box as="section" mt={SECTION_GAP} bg={bandBg} py={{ base: "72px", lg: "112px" }}>
      <ThemedContainer>
        <ClosingCopy headingColor={headingColor} bodyColor={bodyColor} onDarkBand />
      </ThemedContainer>
    </Box>
  );
}
